/**
 * The org's template variables for one surface (`applies_to`), fetched once and
 * shared by every picker on the page. The admin screen calls
 * `invalidateVariableRegistry()` after a write so pickers pick up the change.
 */

import { useEffect, useState } from 'react';
import { templateVariablesApi, type AppliesTo, type TemplateVariable } from './api';

type Key = AppliesTo | 'all';

const cache = new Map<Key, TemplateVariable[]>();
const inflight = new Map<Key, Promise<TemplateVariable[]>>();
const listeners = new Set<() => void>();

function load(key: Key): Promise<TemplateVariable[]> {
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = templateVariablesApi
    .list(key === 'all' ? {} : { applies_to: key })
    .then((data) => {
      const vars = (data.variables ?? []).filter((v) => v.active !== false);
      cache.set(key, vars);
      return vars;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

export function invalidateVariableRegistry() {
  cache.clear();
  listeners.forEach((fn) => fn());
}

export function useVariableRegistry(appliesTo: AppliesTo | 'all' = 'messages') {
  const [variables, setVariables] = useState<TemplateVariable[]>(cache.get(appliesTo) ?? []);
  const [loading, setLoading] = useState(!cache.has(appliesTo));
  const [error, setError] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const bump = () => setVersion((n) => n + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(!cache.has(appliesTo));
    load(appliesTo)
      .then((vars) => {
        if (cancelled) return;
        setVariables(vars);
        setError(false);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliesTo, version]);

  return { variables, loading, error };
}
